import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum';
import { EngagementViewsData } from '@empathybroker/tsclient-stats/dist/models/engagement/views/engagement-views-data';
import { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response';

import { ActionContext, Store } from 'vuex';

import { CONTAINER, TYPES } from '../../../config';

import { DomainEnum, FeatureIdEnum, TaggingTypeEnum } from '../../../enums';

import {
  ActionClass,
  DateFilterSelectedValues,
  FeatureNode,
  FeaturesTree,
  TaggingState,
  TaggingViewData,
  TaggingViewDataItem,
  TaggingViewPayload
} from '../../../models';

import { TaggingUtils } from '../../../utils';

import { ANALYTICS_CONSTS } from '../../analytics/analytics.consts';
import { APP_CONSTS } from '../../app/app.consts';
import { TAGGING_CONSTS } from '../tagging.consts';

export class LoadViewsAction implements ActionClass {

  public static async execute({ commit, rootGetters }: ActionContext<TaggingState, any>, payload: DateFilterSelectedValues): Promise<void> {
    const featuresTree: FeaturesTree = rootGetters[`${ APP_CONSTS.moduleName }/${ APP_CONSTS.getters.featuresTree }`];
    const data = await LoadViewsAction.getData(featuresTree, payload);
    commit(TAGGING_CONSTS.mutations.setData, {
      data,
      type: TaggingTypeEnum.VIEW
    });
  }

  private static async getData(featuresTree: FeaturesTree, period: DateFilterSelectedValues): Promise<TaggingViewData> {
    const featuresTreeCopy = TaggingUtils.removeBrowseAnalyticsFromFeaturesTree(featuresTree);
    return Object.entries(featuresTreeCopy)
      .reduce(async (dataPromise: Promise<TaggingViewData>, [domain, nodes]: [DomainEnum, FeatureNode[]]) => {
        const data = await dataPromise;
        const domainData = await LoadViewsAction.getDomainData(domain, nodes, data, period);
        data.push(...domainData);
        return data;
      }, Promise.resolve([]));
  }

  private static async getDomainData(domain: DomainEnum, nodes: FeatureNode[], data: TaggingViewData,
    period: DateFilterSelectedValues): Promise<TaggingViewData> {
    return nodes.reduce(async (domainDataPromise: Promise<TaggingViewData>, node: FeatureNode) => {
      const domainData = await domainDataPromise;
      const id = TaggingUtils.getTaggingId(node.id as FeatureIdEnum, domain);
      if (!!id) {
        const dataItemRepeated = data.find((dataItem: TaggingViewDataItem) => dataItem.id === id);
        if (!!dataItemRepeated) {
          const domainDataItem = await LoadViewsAction.getDomainData(domain, node.children, data, period);
          dataItemRepeated.children.push(...domainDataItem);
        } else {
          const hasChildren = !!node.children?.length;
          const dataItem = {
            id,
            ...!hasChildren && { context: TaggingUtils.getTaggingContext(domain) },
            count: 0,
            children: []
          };
          const payload = TaggingUtils.getTaggingPayload(node.id as FeatureIdEnum, domain);
          if (hasChildren) {
            dataItem.children = await LoadViewsAction.getDomainData(domain, node.children, data, period);
            dataItem.count =
              dataItem.children.reduce((count: number, dataItemChild: TaggingViewDataItem) => count + dataItemChild.count, 0);
          } else if (!!payload && !!period) {
            try {
              const views = await LoadViewsAction.fetchData(payload, period);
              dataItem.count = views.count;
            } catch (error) {
              console.error(error);
            }
          }
          domainData.push(dataItem);
        }
      }
      return domainData;
    }, Promise.resolve([]));
  }

  private static async fetchData(payload: TaggingViewPayload, period: DateFilterSelectedValues): Promise<EngagementViewsData> {
    const store = CONTAINER.get<Store<any>>(TYPES.Store);
    const engagementViewsResponse: StatsResponse =
      await store.dispatch(`${ ANALYTICS_CONSTS.moduleName }/${ ANALYTICS_CONSTS.actions.fetchStatsReport }`, {
        id: StatsReportsIdsEnum.ENGAGEMENT_VIEWS,
        params: {
          startDate: new Date(period[0]),
          endDate: new Date(period[1]),
          ...payload
        }
      }, { root: true });
    return engagementViewsResponse.data;
  }

}
